import React, { FC, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../app/store';
import Optional from '../../Optional/Optional';
import { useJwtHeaders } from '../../../hooks/useJwtHeaders';
import axios from 'axios';
import { saveGradesAction } from '../../../actions/saveGradesAction';
import './CreateGradesPage.scss';

interface EsrogItem {
  id: number;
  kind: string;
  pitum: boolean;
  year?: string;
}

interface GradeItem {
  id: number;
  grade: string;
  esrog_id?: number;
  esrogId?: number;
  year?: string;
}

const CreateGradesPage: FC = () => {
  const dispatch = useDispatch();
  const headers = useJwtHeaders();
  const admin = useSelector<RootState, boolean>(({ user }) => user.admin!);

  const [esrogs, setEsrogs] = useState<EsrogItem[]>([]);
  const [gradesInputMap, setGradesInputMap] = useState<{ [esrogId: number]: string }>({});
  const [savingEsrogId, setSavingEsrogId] = useState<number | null>(null);

  const fetchData = () => {
    axios
      .get('/api/v1/admin/esrogs', headers)
      .then(({ data: esrogData }) => {
        setEsrogs(esrogData);

        axios
          .get('/api/v1/admin/grades', headers)
          .then(({ data: gradeData }: { data: GradeItem[] }) => {
            populateMap(esrogData, gradeData);
          })
          .catch(() => {
            axios.get('/api/v1/available-items', headers).then(({ data }) => {
              if (data && data.grades) {
                populateMap(esrogData, data.grades);
              }
            });
          });
      })
      .catch(() => {
        axios.get('/api/v1/available-items', headers).then(({ data }) => {
          if (data) {
            if (data.esrogs) setEsrogs(data.esrogs);
            if (data.grades) populateMap(data.esrogs || [], data.grades);
          }
        });
      });
  };

  const populateMap = (esrogList: EsrogItem[], gradeList: GradeItem[]) => {
    const newMap: { [esrogId: number]: string } = {};
    esrogList.forEach(esrog => {
      const matchingGrades = gradeList
        .filter(g => (g.esrog_id ?? g.esrogId) === esrog.id)
        .map(g => g.grade);
      newMap[esrog.id] = matchingGrades.join(', ');
    });
    setGradesInputMap(newMap);
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleInputChange = (esrogId: number, value: string) => {
    setGradesInputMap(prev => ({
      ...prev,
      [esrogId]: value,
    }));
  };

  const handleSaveGrades = (esrogId: number) => {
    const rawString = gradesInputMap[esrogId] || '';
    setSavingEsrogId(esrogId);

    dispatch(
      saveGradesAction(esrogId, rawString, headers, () => {
        setSavingEsrogId(null);
        fetchData();
      })
    );
  };

  const handleClearGrades = (esrogId: number) => {
    if (!window.confirm('Are you sure you want to clear all grades for this Esrog?')) return;
    setGradesInputMap(prev => ({ ...prev, [esrogId]: '' }));
    setSavingEsrogId(esrogId);
    dispatch(
      saveGradesAction(esrogId, '', headers, () => {
        setSavingEsrogId(null);
        fetchData();
      })
    );
  };

  return (
    <div className="container admin-grades-page">
      <Optional renderIf={admin}>
        <h3>Admin: Manage Grades Per Esrog</h3>
        <p className="text-muted">
          Enter comma-separated grades for each Esrog (e.g. <i>Aleph, Beis, Gimel</i> or{' '}
          <i>Standard, Premium</i>) and click Save.
        </p>

        <table className="table table-hover table-striped">
          <thead>
            <tr>
              <th>ID</th>
              <th>Esrog Kind</th>
              <th>Pitum</th>
              <th>Grades (comma-separated)</th>
              <th style={{ width: '130px' }}>Action</th>
            </tr>
          </thead>
          <tbody>
            {esrogs.map(esrog => (
              <tr key={`esrog-grade-${esrog.id}`}>
                <td>{esrog.id}</td>
                <td>
                  <b>{esrog.kind}</b>
                </td>
                <td>{esrog.pitum ? 'Yes' : 'No'}</td>
                <td>
                  <input
                    type="text"
                    className="form-control grades-input"
                    placeholder="e.g. Aleph, Beis, Gimel"
                    value={gradesInputMap[esrog.id] ?? ''}
                    onChange={e => handleInputChange(esrog.id, e.target.value)}
                  />
                </td>
                <td>
                  <div className="d-flex align-items-center gap-2">
                    <button
                      type="button"
                      className="btn btn-success btn-sm"
                      disabled={savingEsrogId === esrog.id}
                      onClick={() => handleSaveGrades(esrog.id)}>
                      {savingEsrogId === esrog.id ? 'Saving...' : 'Save'}
                    </button>
                    <i
                      className="fa delete-icon"
                      title="Clear Grades for this Esrog"
                      onClick={() => handleClearGrades(esrog.id)}>
                      &#xf014;
                    </i>
                  </div>
                </td>
              </tr>
            ))}
            {esrogs.length === 0 && (
              <tr>
                <td colSpan={5} className="text-center text-muted">
                  No Esrogs found to assign grades to.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </Optional>
      <Optional renderIf={!admin}>
        <b>YOU MUST BE AN ADMIN TO VIEW THIS PAGE</b>
      </Optional>
    </div>
  );
};

export default CreateGradesPage;
